// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import { createContext, useEffect, useState } from "react";
import { auth } from "./FirebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { firestore, analytics } from "./FirebaseConfig";
import { doc, updateDoc, serverTimestamp, addDoc, collection } from "firebase/firestore";
import { getUserInfo, getLabelsets, getParametersConfig, getRequiredLicenseAgreements, getSuppliers, getDashboardSettings } from "../shared_components/utils/FirebaseUtils";
import { useTranslation } from "react-i18next";
import { logEvent } from "firebase/analytics";

export const AuthContext = createContext();

// Setting up an app-wide user authentication provider. 
export const AuthContextProvider = ({children}) => {
    const { i18n, t } = useTranslation();
    const [authUser, setAuthUser] = useState({});
    const [ userLoaded, setUserLoaded ] = useState(false);
    const [currentUser, setCurrentUser] = useState({
        uid: null,
        info:{languageCode:"de"}, 
        company:{company_displayname:"", company:"", shift_time:0},
        devices: {},
        device_id: null,
        filter_observation_classes: [],
    });
    const [labelsets, setLabelsets] = useState({});
    const [parametersConfig, setParametersConfig] = useState({});
    const [requiredLicenseAgreements, setRequiredLicenseAgreements] = useState([]);
    const [suppliers, setSuppliers] = useState({});
    const [dashboardSettings, setDashboardSettings] = useState({});

    async function getUserData(user) {
        const currentUserNew = await getUserInfo(firestore, user.uid);
        currentUserNew['uid'] = user.uid;

        // Check if the user has "demo" flag enabled. If so, change display name of company for annonymization. 
        if (currentUserNew.info.user_flags && currentUserNew.info.user_flags.includes('demo')) {
            currentUserNew.company.company_displayname = t('demo_mode_company_displayname');
        }

        if (currentUserNew.company) {
            setLabelsets(await getLabelsets(firestore, currentUserNew.company.company));
            setRequiredLicenseAgreements(await getRequiredLicenseAgreements(firestore));
            setSuppliers(await getSuppliers(firestore, currentUserNew.company.company));
            setDashboardSettings(await getDashboardSettings(firestore, currentUserNew.company.company));
        }

        if (currentUserNew.info.user_flags && currentUserNew.info.user_flags.includes('admin')) {
            setParametersConfig(await getParametersConfig(firestore));
        }

        setCurrentUser(currentUserNew);
        setUserLoaded(true);
        if (currentUserNew.info.languageCode) {
            i18n.changeLanguage(currentUserNew.info.languageCode);
        }
    }

    useEffect(()=>{
        onAuthStateChanged(auth, (user)=>{
            setAuthUser(user);
            setUserLoaded(false);
            if (user) {
                getUserData(user);
            } else {
                setCurrentUser({});
            }
        });
    }, []);

    useEffect(() => {
        /* Log the user's login activity */
        if (!currentUser.uid) return;
        updateDoc(doc(firestore, 'users', currentUser.uid), {last_login: serverTimestamp()});
        logEventCustom("login", {});
    }, [currentUser?.uid]);

    function changeActiveDevice(device_id) {
        /* Change the active device displayed in the dashboard */
        setCurrentUser({...currentUser, device_id: device_id});
        updateDoc(doc(firestore, 'users', currentUser.uid), {selected_device_id: device_id});
    }

    function logEventCustom(eventName, eventParams) {
        /* Log a dashboard event to analytics including our custom event handling */
        // Log the event to Firebase Analytics
        logEvent(analytics, eventName, eventParams);
        // Log the event directly to our database including user ID
        addDoc(collection(firestore, "event_logs"), {
            event_name: eventName,
            event_params: eventParams,
            timestamp: serverTimestamp(),
            user: currentUser.uid,
            client: currentUser.company.company,
            source: "dashboard",
        })
    }

    return(
        // Any component wrapped into "authcontext provider" will be able to reach the current user. 
        // Import into index file to cover the whole application. 
        <AuthContext.Provider value={{
            authUser,
            currentUser,
            userLoaded,
            labelsets,
            parametersConfig,
            requiredLicenseAgreements,
            suppliers,
            dashboardSettings,
            changeActiveDevice,
            logEventCustom,
        }}>
            {children}
        </AuthContext.Provider>
    );
};